
import { useState, useEffect } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { UserCheck, UserX, Search, Copy } from "lucide-react";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const UserManagementTable = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data: profileData, error: profileError } = await supabase
        .from('profile_settings')
        .select(`
          id,
          user_id,
          status,
          api_enabled,
          message_count,
          created_at
        `);

      if (profileError) throw profileError;

      // Get all webhook events - we'll get the most recent one for each user
      const { data: webhookData, error: webhookError } = await supabase
        .from('webhook_events')
        .select('user_id, signing_key')
        .order('created_at', { ascending: false });

      if (webhookError) throw webhookError;

      // Create a map of unique user_id to their most recent signing_key
      const uniqueWebhooks = webhookData?.reduce((acc, webhook) => {
        if (!acc[webhook.user_id]) {
          acc[webhook.user_id] = webhook.signing_key;
        }
        return acc;
      }, {} as Record<string, string>);

      // Combine profile and webhook data
      const userData = profileData.map(profile => ({
        ...profile,
        webhook_key: uniqueWebhooks[profile.user_id]
      }));

      setUsers(userData);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: "Error fetching users",
        description: "Please try again later",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyWebhookUrl = (userId: string, signingKey: string) => {
    const webhookUrl = `${window.location.origin}/api/webhook/${userId}?key=${signingKey}`;
    navigator.clipboard.writeText(webhookUrl);
    toast({
      title: "Webhook URL copied",
      description: "The webhook URL has been copied to your clipboard",
    });
  };

  const toggleUserStatus = async (userId: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      const { error } = await supabase
        .from('profile_settings')
        .update({ status: newStatus })
        .eq('user_id', userId);

      if (error) throw error;

      setUsers(users.map(user => 
        user.user_id === userId ? { ...user, status: newStatus } : user
      ));

      toast({
        title: "Status updated",
        description: `User status changed to ${newStatus}`,
      });
    } catch (error) {
      console.error('Error updating user status:', error);
      toast({
        title: "Error updating status",
        description: "Please try again later",
        variant: "destructive",
      });
    }
  };

  const filteredUsers = users.filter(user =>
    user.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-pink-100">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold bg-gradient-to-r from-hotbot-pink to-hotbot-purple bg-clip-text text-transparent">
          User Management
        </h2>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-pink-50 border-pink-200 focus:border-pink-400"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-4">Loading users...</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User ID</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Messages</TableHead>
              <TableHead>Webhook</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="font-medium">{user.user_id}</TableCell>
                <TableCell>
                  <span className={`px-2 py-1 rounded-full text-sm ${
                    user.status === 'active' 
                      ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-800' 
                      : 'bg-gradient-to-r from-red-100 to-red-200 text-red-800'
                  }`}>
                    {user.status}
                  </span>
                </TableCell>
                <TableCell>{user.message_count || 0}</TableCell>
                <TableCell>
                  {user.webhook_key && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopyWebhookUrl(user.user_id, user.webhook_key)}
                      className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copy URL
                    </Button>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleUserStatus(user.user_id, user.status)}
                      className={user.status === 'active' 
                        ? "text-red-600 hover:text-red-800 hover:bg-red-50"
                        : "text-green-600 hover:text-green-800 hover:bg-green-50"
                      }
                    >
                      {user.status === 'active' ? (
                        <UserX className="w-4 h-4" />
                      ) : (
                        <UserCheck className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};
